import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon } from 'lucide-react';

export const DateRangePicker = ({ startDate, endDate, onRangeChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectionState, setSelectionState] = useState('start'); // 'start' or 'end'
    const [tempStart, setTempStart] = useState(startDate);
    const [tempEnd, setTempEnd] = useState(endDate);
    
    const initialDate = startDate ? new Date(startDate) : new Date();
    const [currentMonth, setCurrentMonth] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

    useEffect(() => {
        setTempStart(startDate);
        setTempEnd(endDate);
    }, [startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isOpen && !(e.target).closest('.date-range-picker-container')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const daysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const firstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const handleDateClick = (date) => {
        const dateStr = formatDate(date);
        
        if (selectionState === 'start') {
            setTempStart(dateStr);
            setTempEnd('');
            setSelectionState('end');
        } else {
            // Second click - set end date
            if (dateStr < tempStart) {
                // If clicked date is before start, swap them
                setTempEnd(tempStart);
                setTempStart(dateStr);
            } else {
                setTempEnd(dateStr);
            }
            setSelectionState('start');
            // Auto-apply the range
            const finalStart = dateStr < tempStart ? dateStr : tempStart;
            const finalEnd = dateStr < tempStart ? tempStart : dateStr;
            onRangeChange(finalStart, finalEnd);
            setIsOpen(false);
        }
    };

    const isInRange = (dateStr) => {
        if (!tempStart || !tempEnd) return false;
        return dateStr >= tempStart && dateStr <= tempEnd;
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        setTempStart('');
        setTempEnd('');
        setSelectionState('start');
        onRangeChange('', '');
    };

    const renderCalendar = () => {
        const days = daysInMonth(currentMonth);
        const startDay = firstDayOfMonth(currentMonth);
        const calendar = [];

        for (let i = 0; i < startDay; i++) {
            calendar.push(<div key={`empty-${i}`} className="h-8 w-8" />);
        }

        for (let i = 1; i <= days; i++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
            const dateStr = formatDate(date);
            const isStart = dateStr === tempStart;
            const isEnd = dateStr === tempEnd;
            const inRange = isInRange(dateStr);

            calendar.push(
                <button
                    key={dateStr}
                    onClick={() => handleDateClick(date)}
                    className={`h-8 w-8 rounded-full text-sm font-medium transition-all duration-200 flex items-center justify-center relative
                        ${isStart || isEnd
                            ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-200 transform scale-105 z-10'
                            : inRange
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'text-slate-600 hover:bg-slate-100'
                        }`}
                >
                    {i}
                </button>
            );
        }

        return calendar;
    };

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const getDisplayText = () => {
        if (!startDate && !endDate) return 'Select Date Range';
        if (startDate && !endDate) return `${new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ...`;
        if (startDate && endDate) {
            if (startDate === endDate) {
                return new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            }
            return `${new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${new Date(endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
        }
        return 'Select Date Range';
    };

    return (
        <div className="relative date-range-picker-container z-50">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-3 py-2 bg-white rounded-xl border transition-all duration-200 w-full
                    ${isOpen || startDate ? 'border-indigo-200 shadow-sm ring-2 ring-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
            >
                <div className={`p-1 rounded-lg ${startDate ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                    <CalendarIcon className="w-4 h-4" />
                </div>
                <span className={`text-sm font-medium flex-1 text-left ${startDate ? 'text-slate-800' : 'text-slate-500'}`}>
                    {getDisplayText()}
                </span>
                {startDate && (
                    <div
                        onClick={clearSelection}
                        className="p-0.5 hover:bg-slate-100 rounded-full text-slate-400 hover:text-red-500 transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </div>
                )}
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-white/95 backdrop-blur-xl border border-slate-200 shadow-xl rounded-2xl p-4 animate-fade-in z-50">
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-bold text-slate-800">
                            {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                        </span>
                        <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 mb-2">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} className="text-center text-xs font-semibold text-slate-400 py-1">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 justify-items-center">
                        {renderCalendar()}
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 text-center">
                            {selectionState === 'start' 
                                ? 'Click to select start date' 
                                : 'Click to select end date'}
                        </p>
                        {tempStart && (
                            <div className="mt-2 flex justify-center gap-2 text-xs">
                                <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">
                                    {tempStart}
                                </span>
                                {tempEnd && (
                                    <>
                                        <span className="text-slate-400">→</span>
                                        <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">
                                            {tempEnd}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
